/**
 * Office Context utilities
 *
 * Extracts SharePoint information from the Office.js context
 * for documents that don't have custom properties.
 */

import { SharePointInfo } from "../types";

/**
 * Check if Office context is available
 */
function isOfficeContextAvailable(): boolean {
  return (
    typeof Office !== "undefined" &&
    Office.context !== undefined &&
    Office.context.document !== undefined
  );
}

/**
 * Get SharePoint information from the current document URL
 */
export function getSharePointInfo(): SharePointInfo {
  if (!isOfficeContextAvailable()) {
    console.log("Office context not available");
    return {
      itemId: null,
      driveId: null,
      webUrl: null,
      fileName: null,
    };
  }

  const url = Office.context.document.url || "";

  return {
    itemId: extractItemIdFromUrl(url),
    driveId: extractDriveIdFromUrl(url),
    webUrl: url || null,
    fileName: extractFileNameFromUrl(url),
  };
}

/**
 * Extract the item ID from a SharePoint document URL
 *
 * SharePoint URLs have formats like:
 * - https://tenant.sharepoint.com/sites/site/_layouts/15/Doc.aspx?sourcedoc={guid}
 * - https://tenant-my.sharepoint.com/personal/user/_layouts/15/Doc.aspx?sourcedoc={guid}
 */
function extractItemIdFromUrl(url: string): string | null {
  // Try to extract sourcedoc (which is the item ID encoded)
  const match = url.match(/sourcedoc=\{?([^}&]+)\}?/i);
  if (match) {
    // Decode URI component to handle %7B...%7D encoded GUIDs
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }
  return null;
}

/**
 * Extract drive ID from URL
 * Note: Drive ID is usually not in the URL, may need Graph API
 */
function extractDriveIdFromUrl(_url: string): string | null {
  // Drive ID is typically not available from URL
  // Would need to call Microsoft Graph API to get it
  return null;
}

/**
 * Extract file name from URL
 */
function extractFileNameFromUrl(url: string): string | null {
  try {
    const parts = url.split("/");
    const lastPart = parts[parts.length - 1];

    // Handle query parameters
    const fileName = lastPart.split("?")[0];

    if (
      fileName.endsWith(".docx") ||
      fileName.endsWith(".doc") ||
      fileName.endsWith(".docm")
    ) {
      return decodeURIComponent(fileName);
    }

    // Try to get from query parameter
    const urlObj = new URL(url);
    const file = urlObj.searchParams.get("file");
    if (file) {
      return decodeURIComponent(file);
    }
  } catch {
    // URL parsing failed
  }
  return null;
}

/**
 * Check if the document is from SharePoint
 */
export function isSharePointDocument(): boolean {
  if (!isOfficeContextAvailable()) {
    return false;
  }
  const url = Office.context.document.url || "";
  return url.includes("sharepoint.com") || url.includes(".sharepoint.");
}

/**
 * Check if Office.js is ready
 */
export function isOfficeReady(): boolean {
  return isOfficeContextAvailable();
}
